<template>
  <div class="form__group form__group--move dropdown"
    :title="currentSelection()"
    :class="getInstanceClass()"
    ref="multiselect">
    <span class="hitzone" @click="isOpen=!isOpen"></span>
    <div class="form__input" :class="isFocused()">
      <input
        :id="`${id}_input`"
        @focus="isOpen=true"
        @blur="onBlur()"
        type="text"
        readonly />
    </div>
    <label :for="`${id}_input`">{{ currentSelection() }}</label>
    <div class="dropdown__content" v-if="isOpen" :class="showComponent()">
      <div class="dropdown__list">
        <div class="dropdown__item"
          v-for="item in checkItems" :key="item.key">
          <LennocCheckbox
            :label="item.label"
            :id="`${id}_item_${item.key}`"
            v-model="item.checked"
            @input="changeStatus({ key: item.key, state: $event })"
            @clicked="onCheckBoxClicked"></LennocCheckbox>
        </div>
      </div>
      <div class="dropdown__listheader"
        v-if="closeDropdownButtonText !== ''">
        <button
          class="button button--sec"
          @click="isOpen=false">{{closeDropdownButtonText}}</button>
      </div>
    </div>
    <LennocIcon image="chevron-bottom"></LennocIcon>
  </div>
</template>

<script lang="ts" setup>
  import { onMounted, onUnmounted, PropType, ref, watch } from 'vue'
  import { MultiSelectItem } from '../../types/types'

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    domclass: {
      type: String,
      default: ''
    },
    items: {
      type: Array as PropType<Array<MultiSelectItem>>,
      default: []
    },
    placeholder: {
      type: String,
      default () {
        return ''
      }
    },
    closeDropdownButtonText: {
      type: String,
      default () {
        return 'Close'
      }
    },
    staticLabel: {
      type: Boolean,
      default: false
    },
    closeDropdownOnCheckboxClick: {
      type: Boolean,
      default () {
        return false
      }
    }
  })

  const emits = defineEmits([
    'input',
    'blur'
  ])

  onMounted(() => {
    document.body.addEventListener('click', handleClickOutside)
    props.items.forEach((el : MultiSelectItem) => {
      addItem(el)
    })
  })

  onUnmounted(() => {
    document.body.removeEventListener('click', handleClickOutside)
  })

  const checkItems = ref<Array<MultiSelectItem>>([])
  const isOpen = ref(false)
  const multiselect = ref()

  const addItem = (item : MultiSelectItem ) => {
    checkItems.value.push({
      key: item.key,
      label: item.label || item.text,
      checked: false
    })
  }

  const changeStatus = (data : Record<string, any>) => {
    const item = checkItems.value.find(i => i.key === data.key)
    if (item) {
      item.checked = data.state
      emits('input', { id: props.id, value: checkedItems().map(i => i.key) })
    }
  }

  const checkedItems = () => {
    return checkItems.value.filter((i : MultiSelectItem) => i.checked)
  }

  const currentSelection = () => {
    const checkedItem = checkedItems().map((i : MultiSelectItem) => i.label).join(', ')
    return checkedItem || props.placeholder
  }

  const getInstanceClass = () => {
    return [
      props.domclass,
      !props.staticLabel ? 'form__group--move' : ''
    ].join(' ')
  }

  const handleClickOutside = (event : Event) => {
    if (!multiselect.value.contains(event.target)) {
      isOpen.value = false
    }
  }

  const onCheckBoxClicked = () => {
    if (props.closeDropdownOnCheckboxClick) {
      isOpen.value = false
    }
  }

const onBlur = () => {
    emits('blur')
  }

  const isFocused = () => {
    return isOpen.value ? 'form__input--focus' : ''
  }

  const showComponent = () => {
    return isOpen.value ? 'dropdown__content--visible' : ''
  }
</script>